import React, { useEffect } from "react";
import Modal from "../Modal";
import Button from "../Button";

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
// #124 react-joyride Time Machine tutorial — Intro modal.
//
// Fires on first login (and each subsequent login until dismissed) for
// users who have `timeTravelAccess === true` AND have not yet completed
// the tutorial. Non-blocking: the user can dismiss with one click and
// continue using the app.
//
// Three exit paths:
//   - Take Tour     → calls onTakeTour() — kicks off TimeMachineTour
//   - Maybe Later   → calls onMaybeLater() — caches a "skip for this
//                     session" flag in sessionStorage; modal will fire
//                     again on the next browser restart but not on a
//                     mid-session tab refresh
//   - Don't Show Again → calls onDontShowAgain() — writes
//                     tutorialState.timeTravel.completed = true to the
//                     Users doc, so it never fires again on any device
//   - Esc / outside click → equivalent to Maybe Later (conservative)
//
// The reset entry in the avatar menu can flip completed back to false
// later if the user wants to re-take it.

const IntroModal = ({
  open,
  onTakeTour,
  onMaybeLater,
  onDontShowAgain,
}) => {
  // Esc key dismisses as Maybe Later (conservative default — don't
  // permanently silence on accidental Esc).
  useEffect(() => {
    if (!open) return;
    const handleKey = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onMaybeLater();
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onMaybeLater]);

  if (!open) return null;

  return (
    <Modal onClose={onMaybeLater}>
      <Modal.Header>
        <span data-tutorial-target="intro-modal-header">
          {"\u{1F552}"} New Feature: Time Machine
        </span>
      </Modal.Header>
      <Modal.Content>
        <div className="w-[520px] flex flex-col gap-3 text-sm">
          <p>
            Your account just got access to <strong>Time Travel</strong> — a
            new feature that lets you see the schedule exactly as it looked
            at any past moment, and trace every change back to who made it
            and when.
          </p>
          <p>
            Would you like a quick tour? It takes about 2 minutes and walks
            through the 12 things you need to know to use Time Travel
            confidently.
          </p>
          <p className="opacity-75 italic">
            You can re-take this tour anytime from your account menu.
          </p>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <div className="flex items-center justify-end gap-2">
          <Button
            color="light"
            className="uppercase"
            onClick={onDontShowAgain}
            data-tutorial-target="intro-dont-show-again"
          >
            Don't Show Again
          </Button>
          <Button
            color="light"
            className="uppercase"
            onClick={onMaybeLater}
            data-tutorial-target="intro-maybe-later"
          >
            Maybe Later
          </Button>
          <Button
            color="primary"
            className="uppercase"
            onClick={onTakeTour}
            data-tutorial-target="intro-take-tour"
          >
            Take Tour
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default IntroModal;
